import { useState } from 'react';
// mantine
import { Loader, Pagination, Table } from '@mantine/core';
import { notifications } from '@mantine/notifications';
// components
import type { TRegistry, TRegistryItem } from '@newo/api/gateway';
import NoRecords from '@/components/NoRecords';
import RegistryItem from '@/pages/VersionControl/components/Registries/components/RegistryItem';
import Drawer from '@/components/Drawer';
import VersionsList from '@/pages/VersionControl/components/Registries/components/VersionsList';
// utils
import {
    useDeleteRegistryItemMutation,
    usePublishRegistryItemMutation
} from '@/services/designer/registry_items/registry_items.queries';
import {
    TVersionControlRegistryItem,
    TVersionControlRegistryVersion,
    useRegistryItemsGroupedByIdnQuery
} from '@/services/bff/bff.queries';
import { openConfirmDeleteModal } from '@/utils/ui';

interface RegistryItemsListProps {
    registry: Pick<TRegistry, 'id' | 'idn'>;
    isProjectsV2Enabled?: boolean;
    searchTerm: string;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
}

const PER_PAGE = 25;

const RegistryItemsList = ({
    registry,
    isProjectsV2Enabled = false,
    searchTerm,
    page,
    setPage
}: RegistryItemsListProps) => {
    const [selectedRegistryItem, setSelectedRegistryItem] = useState<
        TVersionControlRegistryItem | undefined
    >();

    const { data, isSuccess, isLoading, refetch } =
        useRegistryItemsGroupedByIdnQuery(
            registry.id,
            {
                page,
                per: PER_PAGE,
                query: searchTerm
            },
            {
                isProjectsV2Enabled
            }
        );

    const { mutateAsync: deleteRegistryItem } = useDeleteRegistryItemMutation();
    const { isPending: isPublishPending, mutate: publishRegistryItem } =
        usePublishRegistryItemMutation(registry.id, isProjectsV2Enabled);

    const isV2Modules = Array.isArray(data);
    const registryItems = isV2Modules ? data : (data?.items ?? []);
    const total = isV2Modules ? data.length : (data?.metadata.total ?? 0);

    const handlePublish = (item: TVersionControlRegistryVersion) => {
        publishRegistryItem(item.id, {
            onSuccess: () => {
                const publicationAction = isProjectsV2Enabled
                    ? 'published'
                    : 'deployed';
                notifications.show({
                    title: `Module ${item.idn} v${item.version} ${publicationAction}`,
                    message: `Module ${item.idn} v${item.version} ${publicationAction} successfully`,
                    color: 'green'
                });
            }
        });
    };

    const handleDelete = (registryItem: TRegistryItem) => {
        openConfirmDeleteModal({
            title: `Delete ${registryItem.idn} registry item?`,
            children: (
                <>
                    Are you sure you want to delete {registryItem.idn} v
                    {registryItem.version}
                    module?
                </>
            ),
            confirmLabel: 'Delete Registry Item',
            onConfirm: () => {
                deleteRegistryItem(
                    {
                        registryId: registry.id,
                        registryItemId: registryItem.id
                    },
                    {
                        onSuccess: () => {
                            refetch();
                        }
                    }
                );
            }
        });
    };

    return (
        <>
            {isLoading ? (
                <div className="flex justify-center items-center py-8">
                    <Loader size={24} />
                </div>
            ) : undefined}
            {isSuccess && registryItems.length === 0 ? (
                <NoRecords className="py-8" />
            ) : undefined}

            {isSuccess && registryItems.length > 0 ? (
                <div className="rounded-lg border border-solid border-gray-3">
                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Idn</Table.Th>
                                <Table.Th>Latest Version</Table.Th>
                                <Table.Th>Subscriptions</Table.Th>
                                <Table.Th className="w-32" />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody className="[&>tr:last-child]:border-b-0!">
                            {registryItems.map((item) => (
                                <RegistryItem
                                    item={item}
                                    key={item.idn}
                                    onVersionsShow={() => {
                                        setSelectedRegistryItem(item);
                                    }}
                                />
                            ))}
                        </Table.Tbody>
                    </Table>
                    {!isV2Modules && total > PER_PAGE && (
                        <div className="flex justify-center py-4">
                            <Pagination
                                total={Math.ceil(total / PER_PAGE)}
                                value={page}
                                onChange={setPage}
                            />
                        </div>
                    )}
                </div>
            ) : undefined}
            <Drawer
                withOverlay
                name="versions-list"
                opened={!!selectedRegistryItem}
                position="right"
                size="lg"
                title={`${selectedRegistryItem?.idn} versions`}
                onClose={() => {
                    setSelectedRegistryItem(undefined);
                }}
            >
                {selectedRegistryItem ? (
                    <VersionsList
                        isProjectsV2Enabled={isProjectsV2Enabled}
                        isPublishPending={isPublishPending}
                        registryIdn={registry.idn}
                        registryItemIdn={selectedRegistryItem.idn}
                        onDelete={handleDelete}
                        onPublish={handlePublish}
                    />
                ) : undefined}
            </Drawer>
        </>
    );
};

export default RegistryItemsList;
